import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Paper, 
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import axios from 'axios';
import CircleIcon from '@mui/icons-material/Circle';
import InfoIcon from '@mui/icons-material/Info';

// Create an axios instance with default config
const api = axios.create({
  baseURL: window.location.hostname === 'localhost' ? 'https://api.safenetbanking.com' : 'https://api.safenetbanking.com',
  timeout: 10000, // 10 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Error request:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error message:', error.message);
    }
    return Promise.reject(error);
  }
);

export default function AccountStatusControl() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ show: false, text: '', severity: 'success' });

  // Fetch users function (extracted for reuse)
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ 
        show: true, 
        text: `Error fetching users: ${error.response?.data?.error || error.message}`, 
        severity: 'error' 
      });
    }
  };

  // Use the fetchUsers function in useEffect
  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle status change submission
  const handleSubmit = async () => {
    if (!selectedUser) {
      setMessage({ show: true, text: 'Please select an account', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use a direct fetch call with minimal data to avoid any potential issues
      const response = await fetch(`${api.defaults.baseURL}/api/admin/users/${selectedUser}/direct-status-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ s: status }) // Use a very short parameter name
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update status');
      }
      
      // Refresh the user list to show updated statuses
      await fetchUsers();
      
      setMessage({ 
        show: true, 
        text: `Account status successfully updated to ${status === 'A' ? 'Active' : 
               status === 'D' ? 'Dormant/Inactive' : 
               status === 'C' ? 'Closed' : 
               status === 'X' ? 'Disabled' : 
               status === 'S' ? 'Suspicious Login' : 
               status === 'F' ? 'Frozen Account' : 
               status === 'I' ? 'IRS' : status}`, 
        severity: 'success' 
      });
      
      // Reset selection after successful update
      setSelectedUser('');
    } catch (error) {
      setMessage({ 
        show: true, 
        text: 'Failed to update account status: ' + (error.message || 'Unknown error'), 
        severity: 'error' 
      });
      console.error('Error updating status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Status descriptions and colors
  const statusInfo = {
    active: {
      color: 'success.main',
      description: 'This means that the client can access all functions within his/her account'
    },
    dormant: {
      color: 'warning.main',
      description: 'A notice that the account is Dormant/Inactive will be shown on the client\'s dashboard. Also, he/she will not be able to make any transfers.'
    },
    closed: {
      color: 'error.main',
      description: 'When the account is set to Closed, it brings up a message when the client tries to log in, saying that the account no longer exist.'
    },
    disabled: {
      color: 'error.main',
      description: 'A client will be notified when they try logging in that their account has been disabled due to violation of terms. He will be advised to contact the customer care to rectify the issue.'
    },
    suspicious: {
      color: 'warning.main',
      description: 'Marks the account for suspicious login activity. Additional verification may be required.'
    },
    frozen: {
      color: 'info.main',
      description: 'Freezes all transactions on the account until further notice.'
    },
    irs: {
      color: 'error.main',
      description: 'Account marked for IRS review. All transactions will be monitored.'
    }
  };

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
        CONTROL ACCOUNT STATUS
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel id="select-account-label">Select Account</InputLabel>
          <Select
            labelId="select-account-label"
            value={selectedUser}
            label="Select Account"
            onChange={(e) => setSelectedUser(e.target.value)}
            sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
          >
            {users.map(user => (
              <MenuItem key={user.id} value={user.id} sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                {user.full_name} ({user.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl fullWidth>
          <InputLabel id="set-status-label">Set Status</InputLabel>
          <Select
            labelId="set-status-label"
            value={status}
            label="Set Status"
            onChange={(e) => setStatus(e.target.value)}
            sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
          >
            <MenuItem value="A" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Active</MenuItem>
            <MenuItem value="D" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Dormant/Inactive</MenuItem>
            <MenuItem value="C" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Closed</MenuItem>
            <MenuItem value="X" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Disabled</MenuItem>
            <MenuItem value="S" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Suspicious Login</MenuItem>
            <MenuItem value="F" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Frozen Account</MenuItem>
            <MenuItem value="I" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>IRS</MenuItem>
          </Select>
        </FormControl>
        
        <Button 
          variant="contained" 
          onClick={handleSubmit} 
          disabled={loading}
          sx={{ 
            minWidth: { xs: '100%', md: '100px' }, 
            height: { xs: '40px', md: '56px' },
            alignSelf: { xs: 'center', md: 'flex-end' },
            mt: { xs: 1, md: 0 }
          }}
        >
          Set
        </Button>
      </Box>
      
      {message.show && (
        <Alert 
          severity={message.severity} 
          sx={{ mb: 3, fontSize: { xs: '0.875rem', md: '1rem' } }}
          onClose={() => setMessage({ ...message, show: false })}
        >
          {message.text}
        </Alert>
      )}
      
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
          Status Descriptions
        </Typography>
        <List sx={{ 
          maxHeight: { xs: '300px', md: 'none' }, 
          overflowY: { xs: 'auto', md: 'visible' },
          border: { xs: '1px solid #eee', md: 'none' },
          borderRadius: { xs: '4px', md: 0 }
        }}>
          {Object.entries(statusInfo).map(([key, info]) => (
            <ListItem key={key} sx={{ py: { xs: 1, md: 1.5 } }}>
              <ListItemIcon sx={{ minWidth: { xs: '36px', md: '56px' } }}>
                <CircleIcon sx={{ color: info.color, fontSize: { xs: 12, md: 14 } }} />
              </ListItemIcon>
              <ListItemText 
                primary={key.charAt(0).toUpperCase() + key.slice(1)} 
                secondary={info.description}
                primaryTypographyProps={{ fontSize: { xs: '0.9rem', md: '1rem' }, fontWeight: 'medium' }}
                secondaryTypographyProps={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Paper>
  );
}
